import {
  SearchInput,
  TableCellContentRenderer,
  TableHeaderContentRenderer,
  Tooltip
} from '@/components/basic';
import { ApprovableCell } from '@/components/permissions/ApprovableCell';
import PermissionCell from '@/components/permissions/PermissionCell';
import { PermissionsDatesCell } from '@/components/permissions/PermissionsDatesCell';
import {
  ConfirmOperationModal,
  IResourcePermissionWithResName
} from '@/components/resources/tabs/access-restrictions/RestrictionsTab';
import { classNames } from '@/lib/classNames';
import { IResourcePermission } from '@gettactic/api';
import { ScrollArea } from '@mantine/core';
import React, { useMemo, useState } from 'react';
import { MdDelete } from 'react-icons/md';
import { Column, Row, useTable } from 'react-table';
import { Checkbox } from 'reakit';

type Props = {
  className?: string;
  data: IResourcePermissionWithResName[];
  setOperationalModal: React.Dispatch<ConfirmOperationModal | null>;
};

function bulkKey(perm: IResourcePermission) {
  return `${perm.id}_${perm.resource_id}_${perm.user ? 'user' : 'team'}`;
}

export const PermissionTable = ({
  className,
  data: rawData,
  setOperationalModal
}: Props): JSX.Element => {
  const [query, setQuery] = useState('');
  const [bulk, setBulk] = useState<string[]>([]);
  const data = useMemo(() => {
    const q = query.toLowerCase().trim();
    return q.length
      ? rawData.filter(
          (x) =>
            x.resName.toLowerCase().includes(q) ||
            (x.user
              ? x.user.name.toLowerCase().includes(q) ||
                x.user.email.toLowerCase().includes(q)
              : x.team?.name.toLowerCase().includes(q))
        )
      : rawData;
  }, [query, rawData]);
  const selectedAll = data.length ? data.length === bulk.length : false;
  const visibleSelected = data.filter((x) => bulk.indexOf(bulkKey(x)) > -1);

  const columns: Column<IResourcePermission>[] = useMemo(
    () =>
      [
        {
          Header() {
            return (
              <TableHeaderContentRenderer>
                <div className="px-2">
                  <Checkbox
                    className="rounded text-primary focus:ring-0"
                    checked={selectedAll}
                    onClick={() => {
                      if (!selectedAll) {
                        setBulk((x) => data.map((x) => bulkKey(x)));
                      } else {
                        setBulk([]);
                      }
                    }}
                  />
                </div>
              </TableHeaderContentRenderer>
            );
          },
          accessor: undefined,
          id: 'selected',
          Cell({
            row: { original: permission }
          }: { row: Row<IResourcePermission> }) {
            const selected = bulk.indexOf(bulkKey(permission)) > -1;
            return (
              <div className="flex items-center justify-start space-x-2 px-2">
                <Checkbox
                  className="rounded text-primary focus:ring-0"
                  checked={selected}
                  onClick={() => {
                    if (selected) {
                      setBulk((x) =>
                        x.filter((s) => s !== bulkKey(permission))
                      );
                    } else {
                      setBulk((x) => [...x, bulkKey(permission)]);
                    }
                  }}
                />
              </div>
            );
          }
        },
        {
          Header() {
            return (
              <TableHeaderContentRenderer>
                <div>Assignment</div>
              </TableHeaderContentRenderer>
            );
          },
          accessor: undefined,
          id: 'type',
          maxWidth: 410,
          Cell({
            row: { original: permission },
            column
          }: { row: Row<IResourcePermission>, column: Column<IResourcePermission> }) {
            return <PermissionCell permission={permission} column={column} />;
          }
        },
        {
          Header() {
            return (
              <TableHeaderContentRenderer>
                <div>Dates</div>
              </TableHeaderContentRenderer>
            );
          },
          accessor: undefined,
          id: 'dates',
          Cell: PermissionsDatesCell
        },
        {
          Header() {
            return (
              <TableHeaderContentRenderer>
                <div title="Resource">Res.</div>
              </TableHeaderContentRenderer>
            );
          },
          accessor: undefined,
          id: 'resource',
          Cell({
            row: { original: permission }
          }: { row: Row<IResourcePermissionWithResName> }) {
            return (
              <div className="flex items-center justify-center space-x-1 text-center text-sm">
                {permission.resName}
              </div>
            );
          }
        },
        {
          Header() {
            return (
              <TableHeaderContentRenderer>
                <div title="Resource">Group</div>
              </TableHeaderContentRenderer>
            );
          },
          accessor: undefined,
          id: 'accessGroupType',
          Cell({
            row: { original: permission }
          }: { row: Row<IResourcePermissionWithResName> }) {
            return (
              <div className="flex items-center justify-center space-x-1 text-center text-sm">
                {permission.access_group_type}
              </div>
            );
          }
        },
        {
          Header: (
            <div className="flex items-center">
              <Tooltip>
                Approval Required
                <span className="font-normal">
                  {' '}
                  Users will need a manager to approve a reservation before it's
                  accepted by the system.
                </span>
              </Tooltip>
            </div>
          ),
          id: 'edit-approvable-status',
          accessor: undefined,
          Cell: ({
            row: { original: permission }
          }: { row: Row<IResourcePermission> }) => {
            return (
              <ApprovableCell
                permission={permission}
                onChange={(x: IResourcePermission) => {
                  setOperationalModal(
                    x ? { operation: 'is_approvable', applyTo: [x] } : null
                  );
                }}
              />
            );
          }
        },
        {
          Header: '',
          id: 'edit-menu-placeholder',
          accessor: undefined,
          Cell: ({ row }: { row: Row<IResourcePermission> }) =>
            TableCellContentRenderer.EditDeleteControls<IResourcePermission>({
              row,
              setDeleteModal: (x) =>
                setOperationalModal(
                  x ? { operation: 'delete', applyTo: [x] } : null
                )
            })
        }
      ] as Column<IResourcePermission>[],
    [bulk, data, selectedAll]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    flexRender
  } = useTable<IResourcePermission>({
    columns,
    data
  });

  return (
    <div className="flex flex-1 flex-col flex-nowrap items-stretch gap-3 overflow-hidden px-1">
      <div className="flex items-center justify-between gap-4 sm:gap-16">
        <SearchInput query={query} setQuery={setQuery} />
        <div className="flex flex-nowrap">
          {visibleSelected.length ? (
            <p>Selected {visibleSelected.length}</p>
          ) : null}
          <button
            disabled={bulk.length === 0}
            onClick={(ev) => {
              ev.preventDefault();
              setOperationalModal({
                operation: 'delete',
                applyTo: visibleSelected
              });
            }}
            className={classNames(
              visibleSelected.length
                ? 'text-gray-600 hover:text-gray-800'
                : 'text-gray-100',
              'px-2'
            )}
          >
            <MdDelete className="h-5 w-5" />
          </button>
        </div>
      </div>
      <ScrollArea
        type="auto"
        offsetScrollbars
        className="h-full"
        classNames={{
          root: 'rounded-lg border border border-gray-300'
        }}
      >
        <div className={classNames('flex flex-col', className)}>
          <div className="flex flex-auto flex-col overflow-y-auto">
            <div className="overflow-hidden rounded-md border bg-white shadow-sm">
              <table className="w-full" {...getTableProps()}>
                <thead>
                  {headerGroups.map((headerGroup) => (
                    // eslint-disable-next-line react/jsx-key                        
// biome-ignore lint/correctness/useJsxKeyInIterable: <key's are in spread>
                    // biome-ignore lint/correctness/useJsxKeyInIterable: <explanation>
                    <tr
                      className="border-gray-300 border-b bg-gray-50"
                      {...headerGroup.getHeaderGroupProps()}
                    >
                      {headerGroup.headers.map((column) => (
                        // eslint-disable-next-line react/jsx-key                        
// biome-ignore lint/correctness/useJsxKeyInIterable: <key's are in spread>
                        // biome-ignore lint/correctness/useJsxKeyInIterable: <explanation>
                        <th
                          className="py-3 text-left font-bold text-gray-800"
                          {...column.getHeaderProps()}
                        >
                          {column.render('Header')}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                  {rows.map((row, index) => {
                    prepareRow(row);
                    return (
                      <tr
                        className="border-gray-300 border-t border-b bg-white last:border-b-0"
                        {...row.getRowProps()}
                        key={index}
                      >
                        {row.cells.map((cell, index) => (
                          <td {...cell.getCellProps()} key={index}>
                            {cell.render('Cell')}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
};
